<script setup lang="ts">
import { watchDebounced } from "@vueuse/core";
import { GetKioskGamePlays, GetKioskGames } from "@/api/gamesApi";
import GamePlaysTable from "@/components/games/GamePlaysTable.vue";
import GamesDemoAlert from "@/components/games/GamesDemoAlert.vue";
import EntityListToolbar from "@/components/shared/EntityListToolbar.vue";
import { useGamesFormat } from "@/composables/useGamesFormat";
import {
  PLAY_STATUSES,
  type KioskGame,
  type KioskGamePlay,
  type PlayStatus,
} from "@/type/games";

/**
 * Every play across all games — who won what and whether reception has honoured it.
 * Search matches a claim code, the last digits of a phone, or a prize.
 */

definePage({ meta: { permissionKey: "page.games.plays" } });

const route = useRoute();
const { t } = useI18n({ useScope: "global" });
const { gameTitle, playStatusLabel } = useGamesFormat();

const plays = ref<KioskGamePlay[]>([]);
const games = ref<KioskGame[]>([]);
const loading = ref(false);
const failed = ref(false);

const search = ref("");
const gameFilter = ref<string | null>(typeof route.query.game === "string" ? route.query.game : null);
const statusFilter = ref<PlayStatus | null>(null);

const gameItems = computed(() => games.value.map(g => ({ value: g.name, title: gameTitle(g) })));
const statusItems = computed(() => PLAY_STATUSES.map(value => ({ value, title: playStatusLabel(value) })));

async function load() {
  failed.value = false;
  try {
    plays.value = await GetKioskGamePlays(loading, {
      game: gameFilter.value,
      status: statusFilter.value,
      search: search.value,
    });
  }
  catch {
    failed.value = true;
  }
}

watch([gameFilter, statusFilter], load);
watchDebounced(search, load, { debounce: 300 });

onMounted(async () => {
  void load();
  try {
    games.value = await GetKioskGames(ref(false));
  }
  catch {
    games.value = [];
  }
});
</script>

<template>
  <div class="plays-page">
    <div class="mb-4">
      <h1 class="text-h4">
        {{ t("alkokh.games.plays.title") }}
      </h1>
      <p class="text-body-2 text-medium-emphasis mb-0">
        {{ t("alkokh.games.plays.subtitle") }}
      </p>
    </div>

    <GamesDemoAlert @reset="load" />

    <VAlert
      v-if="failed"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ t("alkokh.games.messages.loadFailed") }}
      <template #append>
        <VBtn
          size="small"
          variant="text"
          @click="load"
        >
          {{ t("alkokh.global.retry") }}
        </VBtn>
      </template>
    </VAlert>

    <VCard elevation="0">
      <EntityListToolbar
        :search="search"
        :search-placeholder="t('alkokh.games.plays.searchPlaceholder')"
        :show-add-button="false"
        @update:search="search = $event"
      >
        <template #filters>
          <AppSelect
            v-model="gameFilter"
            :items="gameItems"
            clearable
            density="compact"
            :placeholder="t('alkokh.games.plays.allGames')"
            style="min-inline-size: 200px;"
          />
          <AppSelect
            v-model="statusFilter"
            :items="statusItems"
            clearable
            density="compact"
            :placeholder="t('alkokh.games.list.allStatuses')"
            style="min-inline-size: 160px;"
          />
        </template>
      </EntityListToolbar>

      <VDivider />

      <VCardText>
        <GamePlaysTable
          :rows="plays"
          :loading="loading"
          :filtered="Boolean(search || gameFilter || statusFilter)"
        />
      </VCardText>
    </VCard>
  </div>
</template>
